import React from 'react';
import SharedPageLayout from './layout/SharedPageLayout';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Rocket, Sparkles, Bug, Zap, Shield, Users } from 'lucide-react';

const Changelog = ({ onBack, onLogin, onRegister, navigateTo }) => {
  const releases = [
    {
      version: '3.2.0',
      date: 'February 2026',
      type: 'feature',
      title: 'Enterprise Workspaces',
      changes: [
        'Multi-tenant workspace system with custom subdomains',
        'Cross-domain authentication for seamless login',
        'Workspace-scoped data isolation',
        'Custom branding options per workspace'
      ]
    },
    {
      version: '3.1.0',
      date: 'January 2026',
      type: 'feature',
      title: 'API Test Chains',
      changes: [
        'Chain multiple API tests into workflows',
        'Variable extraction and data passing between tests',
        'Conditional execution with stop-on-fail',
        'Visual workflow builder'
      ]
    },
    {
      version: '3.0.0',
      date: 'December 2025',
      type: 'major',
      title: 'Sprint QA Module',
      changes: [
        'Full QA lifecycle management',
        'Requirements → Test Scenarios → Test Cases hierarchy',
        'Test execution tracking with evidence upload',
        'AI-powered test case generation'
      ]
    },
    {
      version: '2.5.0',
      date: 'November 2025',
      type: 'feature',
      title: 'Visual Flow Builder',
      changes: [
        'Drag-and-drop test flow creation',
        'Live VNC viewer for test execution',
        'Step recording from browser actions',
        'Reusable test components'
      ]
    },
    {
      version: '2.0.0',
      date: 'September 2025',
      type: 'major',
      title: 'AI Test Generation',
      changes: [
        'Natural language test descriptions',
        'Automatic Playwright code generation',
        'Smart selector detection',
        'Test optimization suggestions'
      ]
    },
    {
      version: '1.5.0',
      date: 'August 2025',
      type: 'feature',
      title: 'Platform Launch',
      changes: [
        'Initial public release',
        'API testing capabilities',
        'Team collaboration features',
        'CI/CD integrations'
      ]
    }
  ];

  const getTypeBadge = (type) => {
    switch (type) {
      case 'major':
        return <Badge className="bg-purple-100 text-purple-700">Major Release</Badge>;
      case 'feature':
        return <Badge className="bg-blue-100 text-blue-700">New Features</Badge>;
      case 'fix':
        return <Badge className="bg-green-100 text-green-700">Bug Fixes</Badge>;
      case 'security':
        return <Badge className="bg-red-100 text-red-700">Security</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">Update</Badge>;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'major':
        return <Rocket className="w-5 h-5 text-purple-600" />;
      case 'feature':
        return <Sparkles className="w-5 h-5 text-blue-600" />;
      case 'fix':
        return <Bug className="w-5 h-5 text-green-600" />;
      case 'security':
        return <Shield className="w-5 h-5 text-red-600" />;
      default:
        return <Zap className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <SharedPageLayout
      title="Changelog - Product Updates | Lexa.run"
      description="See what's new in Lexa.run. Latest features, improvements, and bug fixes for our AI-powered test automation platform."
      onBack={onBack}
      onLogin={onLogin}
      onRegister={onRegister}
      navigateTo={navigateTo}
    >
      {/* Hero Section */}
      <section className="pt-16 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="outline" className="mb-4 text-purple-600 border-purple-200">
            Product Updates
          </Badge>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
              Changelog
            </span>
          </h1>
          <p className="text-xl text-gray-600">
            Stay up to date with the latest features, improvements, and fixes.
          </p>
        </div>
      </section>

      {/* Releases */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {releases.map((release, index) => (
              <Card key={index} className="p-8 border-gray-200/50">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                    {getTypeIcon(release.type)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <h2 className="text-xl font-bold text-gray-900">
                        v{release.version} - {release.title}
                      </h2>
                      {getTypeBadge(release.type)}
                    </div>
                    
                    <p className="text-sm text-gray-500 mb-4">{release.date}</p>
                    
                    <ul className="space-y-2">
                      {release.changes.map((change, changeIndex) => (
                        <li key={changeIndex} className="flex items-start gap-2 text-gray-600">
                          <span className="text-purple-500 mt-1">•</span>
                          {change}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Stay Updated
          </h2>
          <p className="text-gray-600 mb-6">
            Get notified about new features and updates.
          </p>
          <a 
            href="mailto:updates@lexa.run?subject=Subscribe to Updates"
            className="inline-flex items-center px-6 py-3 rounded-full bg-purple-600 text-white hover:bg-purple-700 font-medium transition-colors"
          >
            Subscribe to Updates
          </a>
        </div>
      </section>
    </SharedPageLayout>
  );
};

export default Changelog;
